import {
	getSecuritiesOptionsForSelect,
	TRANSACTION_TYPE_OPTIONS,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import {
	EDIT_TRANSACTION_FORM_FIELD,
	useEditTransactionDrawerController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/EditTransactionDrawer/useEditTransactionDrawerController';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Alert, Button, Drawer, Stack, Text, TextInput } from '@mantine/core';
import { getTransactionInterestText } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/getTransactionInterestText.ts';
import { useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { NumberInput, Select } from 'react-hook-form-mantine';
import { Link } from 'react-router-dom';
import {
	DataSecurityTct,
	GetTransactionRes,
	TransactionType,
	TransactionWithSecurityTct,
} from '@dealstack/shared/lib/generated';
import { StyledForm } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/styled.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { shouldShowHoldsFor } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/hooks/useTransactionFieldsVisibility';
import { isManagementLoanSecurity } from '@dealstack/frontend/shared/cap';
import { InterestPairEditModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/InterestPairEditModal/InterestPairEditModal.tsx';
import { InseparableRedemptionModal } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/InseparableRedemptionModal/InseparableRedemptionModal.tsx';
import { RedemptionCounterpartPanel } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/RedemptionCounterpartPanel/RedemptionCounterpartPanel.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	securities?: DataSecurityTct[];
	refreshTransactionsList: () => void;
	transaction: GetTransactionRes;
	// CAP-1976: escape hatch out of a blocked interest-pair edit. The list owns the delete
	// confirmation, so the drawer only asks for it.
	onDeletePair: () => void;
}

export const EditTransactionDrawer = ({
	opened,
	onClose,
	securities,
	refreshTransactionsList,
	transaction,
	onDeletePair,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const {
		updateTransactionsRequest,
		editTransactionForm,
		sellerMipSelectOptions,
		onSubmit,
		personsSelectOptions,
		mipSelectOptions,
		hideBoughtFor,
		hideSoldFor,
		boughtForOptions,
		soldForOptions,
		hideUnits,
		hideSeller,
		hideBuyer,
		hideBuyerMip,
		hideSellerMip,
		costLabel,
		unitsDecimalPoints,
		hideCost,
		showInterestSelect,
		operationInterestOptions,
		pairInterestOptions,
		operationRailNote,
		interestFixedByRedemptionGroup,
		interestFixedReason,
		interestPairModal,
		closeInterestPairModal,
		nomineeStructuresEnabled,
		isRedemptionOperation,
		selectedSecurity,
		redemptionCounterparts,
		redemptionCounterpartPreview,
		redemptionPreviewInterest,
		redemptionSegmentCount,
		pendingRedemption,
		confirmRedemptionOperation,
		cancelRedemptionOperation,
		isRedemptionOperationPending,
	} = useEditTransactionDrawerController({
		transaction: transaction as TransactionWithSecurityTct,
		refreshTransactionsList,
		onClose,
		securities,
	});

	const isManagementLoan = isManagementLoanSecurity(selectedSecurity);

	const securityNameSelectOptions = useMemo(
		() => getSecuritiesOptionsForSelect(securities),
		[securities]
	);

	const buyerMip = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.BUYER_MIP);
	const sellerMip = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.SELLER_MIP);

	useEffect(() => {
		const noMipOption = mipSelectOptions.find((buyerMip) => !buyerMip.value);
		if (!buyerMip && noMipOption) {
			editTransactionForm.setValue(EDIT_TRANSACTION_FORM_FIELD.BUYER_MIP, noMipOption.value);
		}
	}, [buyerMip, mipSelectOptions]);

	useEffect(() => {
		const noMipOption = sellerMipSelectOptions.find((sellerMip) => !sellerMip.value);
		if (!sellerMip && noMipOption) {
			editTransactionForm.setValue(EDIT_TRANSACTION_FORM_FIELD.SELLER_MIP, noMipOption.value);
		}
	}, [sellerMip, sellerMipSelectOptions]);

	const transactionType = editTransactionForm.watch(EDIT_TRANSACTION_FORM_FIELD.TYPE);
	// CAP-1062: the feature flag (read once, in the controller) keeps every nominee surface dark,
	// including the read-only interest field on already-titled transactions.
	// Read-only fallback for the transactions whose interest is not selectable — an ISSUE (both titles
	// share the serials, so a split is only reachable via the paired issue) or a security outside a
	// nominee structure. Everything in isNomineeInterestEditable gets the dropdown above instead.
	const interestLabel = nomineeStructuresEnabled
		? getTransactionInterestText(transaction.title, tCapManage)
		: null;
	const securityLabel =
		transactionType === TransactionType.CONVERSION_SHARE_CAPITAL
			? tCapManage('transactions.drawer.from.label')
			: tCapManage('transactions.drawer.security_name.label');

	const buyerLabel = useMemo(() => {
		if (transactionType === TransactionType.SHAREHOLDER_CONTRIBUTION) {
			return tCapManage('transactions.drawer.contributor.label');
		}
		return tCapManage('transactions.drawer.buyer.label');
	}, [transactionType, tCapManage]);

	const buyerPlaceholder = useMemo(() => {
		if (transactionType === TransactionType.SHAREHOLDER_CONTRIBUTION) {
			return tCapManage('transactions.drawer.contributor.placeholder');
		}
		return tCapManage('transactions.drawer.buyer.placeholder');
	}, [transactionType, tCapManage]);

	const sellerLabel = useMemo(() => {
		if (transactionType === TransactionType.REDUCTION_IN_SHARE_CAPITAL) {
			return tCapManage('transactions.drawer.recipient_capital.label');
		}
		if (transactionType === TransactionType.DIVIDEND) {
			return tCapManage('transactions.drawer.recipient_dividend.label');
		}
		return tCapManage('transactions.drawer.seller.label');
	}, [transactionType, tCapManage]);

	const sellerPlaceholder = useMemo(() => {
		if (transactionType === TransactionType.REDUCTION_IN_SHARE_CAPITAL) {
			return tCapManage('transactions.drawer.recipient_capital.placeholder');
		}
		if (transactionType === TransactionType.DIVIDEND) {
			return tCapManage('transactions.drawer.recipient_dividend.placeholder');
		}
		return tCapManage('transactions.drawer.seller.placeholder');
	}, [transactionType, tCapManage]);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={tCapManage('transactions.drawer.edit_title')}
		>
			{updateTransactionsRequest.isPending && <InlineLoader />}
			<FormProvider {...editTransactionForm}>
				<StyledForm onSubmit={editTransactionForm.handleSubmit(onSubmit)}>
					<MantineDateInput
						name={EDIT_TRANSACTION_FORM_FIELD.DATE}
						maxDate={new Date()}
						label={tCapManage('transactions.drawer.date.label')}
						size="md"
					/>
					<Select
						name={EDIT_TRANSACTION_FORM_FIELD.SECURITY_ID}
						control={editTransactionForm.control}
						data={securityNameSelectOptions}
						label={securityLabel}
						placeholder={tCapManage('transactions.drawer.security_name.placeholder')}
						size="md"
						searchable
					/>
					<Select
						name={EDIT_TRANSACTION_FORM_FIELD.TYPE}
						control={editTransactionForm.control}
						data={TRANSACTION_TYPE_OPTIONS}
						label={tCapManage('transactions.drawer.transaction_type.label')}
						placeholder={tCapManage('transactions.drawer.transaction_type.placeholder')}
						size="md"
						clearable={false}
					/>
					{showInterestSelect ? (
						<Select
							name={EDIT_TRANSACTION_FORM_FIELD.TITLE}
							control={editTransactionForm.control}
							data={
								isRedemptionOperation
									? operationInterestOptions
									: pairInterestOptions
							}
							data-testid={
								TEST_ELEMENT_IDS.CAP_TRANSACTION_EDIT_TRANSFER_INTEREST_SELECT
							}
							label={tCapManage('transactions.drawer.interest.label')}
							placeholder={tCapManage('transactions.drawer.interest.placeholder')}
							size="md"
							clearable={false}
							/* CAP-1976: in operation mode the interest is editable — the server
							   re-resolves the whole redemption against it. Outside it (a detail-page
							   row edit) a group leg's interest is still fixed, shown disabled WITH
							   the reason rather than silently absent. */
							disabled={interestFixedByRedemptionGroup && !isRedemptionOperation}
							description={isRedemptionOperation ? undefined : interestFixedReason}
						/>
					) : (
						interestLabel && (
							<TextInput
								label={tCapManage('transactions.drawer.interest.label', {
									defaultValue: 'Interest',
								})}
								value={interestLabel}
								readOnly
								disabled
								size="md"
							/>
						)
					)}
					{transactionType === TransactionType.CONVERSION_SHARE_CAPITAL && (
						<Select
							name={EDIT_TRANSACTION_FORM_FIELD.CONVERSION_TARGET_SECURITY_ID}
							control={editTransactionForm.control}
							data={securityNameSelectOptions}
							label={tCapManage('transactions.drawer.to.label')}
							placeholder={tCapManage(
								'transactions.drawer.conversion_target_security.placeholder'
							)}
							size="md"
							searchable
						/>
					)}
					{!hideBuyer && !isRedemptionOperation && (
						<Select
							name={EDIT_TRANSACTION_FORM_FIELD.BUYER}
							control={editTransactionForm.control}
							data={personsSelectOptions}
							label={buyerLabel}
							placeholder={buyerPlaceholder}
							size="md"
							searchable
							clearable={false}
						/>
					)}
					{shouldShowHoldsFor({
						hideHoldsFor: hideBoughtFor,
						partyIsSecurityIssuer: hideBuyer,
						isManagementLoan,
						storedHoldsForId: transaction.boughtForId,
					}) && (
						<Select
							name={EDIT_TRANSACTION_FORM_FIELD.BOUGHT_FOR}
							control={editTransactionForm.control}
							data={boughtForOptions}
							label={tCapManage('transactions.drawer.bought_for.label')}
							placeholder={tCapManage('transactions.drawer.bought_for.placeholder')}
							size="md"
							searchable
							clearable={true}
						/>
					)}
					{!hideSeller && (
						<Select
							name={EDIT_TRANSACTION_FORM_FIELD.SELLER}
							control={editTransactionForm.control}
							label={sellerLabel}
							placeholder={sellerPlaceholder}
							data={personsSelectOptions}
							size="md"
							searchable
							clearable={false}
						/>
					)}
					{shouldShowHoldsFor({
						hideHoldsFor: hideSoldFor,
						partyIsSecurityIssuer: hideSeller,
						isManagementLoan,
						storedHoldsForId: transaction.soldForId,
					}) && (
						<Select
							name={EDIT_TRANSACTION_FORM_FIELD.SOLD_FOR}
							control={editTransactionForm.control}
							label={tCapManage('transactions.drawer.sold_for.label')}
							placeholder={tCapManage('transactions.drawer.sold_for.placeholder')}
							data={soldForOptions}
							size="md"
							searchable
							clearable={true}
						/>
					)}
					{!hideBuyerMip && (
						<Select
							name={EDIT_TRANSACTION_FORM_FIELD.BUYER_MIP}
							control={editTransactionForm.control}
							label={tCapManage('transactions.drawer.buyer_mip.label')}
							placeholder={tCapManage('transactions.drawer.buyer_mip.label')}
							data={mipSelectOptions}
							size="md"
							searchable
							readOnly={!!transaction.pgSaId}
							disabled={!!transaction.pgSaId}
							clearable={false}
						/>
					)}
					{!hideSellerMip && (
						<Select
							name={EDIT_TRANSACTION_FORM_FIELD.SELLER_MIP}
							control={editTransactionForm.control}
							label={tCapManage('transactions.drawer.seller_holding.label')}
							placeholder={tCapManage(
								'transactions.drawer.seller_holding.placeholder'
							)}
							data={sellerMipSelectOptions}
							size="md"
							searchable
							readOnly={!!transaction.pgSaId || sellerMipSelectOptions.length <= 1}
							disabled={!!transaction.pgSaId || sellerMipSelectOptions.length <= 1}
							clearable={false}
						/>
					)}

					{!hideUnits && (
						<NumberInput
							key={`units-${unitsDecimalPoints}`} // this ensures that field is rerendered when precision changes
							name={EDIT_TRANSACTION_FORM_FIELD.NUMBER}
							control={editTransactionForm.control}
							label={tCapManage('transactions.drawer.number_of_units.label')}
							placeholder={tCapManage(
								'transactions.drawer.number_of_units.placeholder'
							)}
							decimalScale={unitsDecimalPoints}
							size="md"
						/>
					)}
					{!hideCost && (
						<NumberInput
							name={EDIT_TRANSACTION_FORM_FIELD.PRICE}
							control={editTransactionForm.control}
							label={costLabel || tCapManage('transactions.drawer.price.label')}
							placeholder={tCapManage('transactions.drawer.price.placeholder')}
							size="md"
							step={0.01}
							decimalScale={2}
						/>
					)}

					{operationRailNote && (
						<Alert color="gray" variant="light">
							<Text size="sm">{operationRailNote}</Text>
						</Alert>
					)}

					{redemptionCounterparts.length > 0 && (
						<Alert
							color="blue"
							variant="light"
							title={tCapManage('transactions.drawer.retires_alongside.title')}
						>
							<Stack gap={4}>
								{redemptionCounterparts.map((counterpart) => (
									<Text
										key={`${counterpart.holderName}-${counterpart.title}`}
										size="sm"
									>
										{counterpart.holderName} (
										{getTransactionInterestText(counterpart.title, tCapManage)}
										), {counterpart.units}
									</Text>
								))}
								{redemptionSegmentCount > 1 && (
									<Text size="xs" c="dimmed">
										{tCapManage(
											'transactions.drawer.retires_alongside.spread',
											{
												count: redemptionSegmentCount,
											}
										)}
									</Text>
								)}
							</Stack>
						</Alert>
					)}

					{/* CAP-2039: what the values CURRENTLY in the form would retire, as opposed to what
					    the group retires today (the panel above). Every field the operation editor
					    exists to change moves the counterpart, so the new answer has to be visible
					    before Save rather than in the confirmation after it. */}
					<RedemptionCounterpartPanel
						preview={redemptionCounterpartPreview}
						interest={redemptionPreviewInterest}
						securityName={selectedSecurity?.name ?? ''}
					/>

					<Button type="submit" fullWidth mt="auto">
						{tCapManage('transactions.drawer.save_button')}
					</Button>

					<Link to={`./${transaction.id}`}>
						<Button type="submit" fullWidth variant="default">
							More details
						</Button>
					</Link>
				</StyledForm>
			</FormProvider>

			{pendingRedemption ? (
				<InseparableRedemptionModal
					disclosure={pendingRedemption.disclosure}
					onProceed={() => void confirmRedemptionOperation()}
					onCancel={cancelRedemptionOperation}
					isSubmitting={isRedemptionOperationPending}
				/>
			) : null}

			{interestPairModal ? (
				<InterestPairEditModal
					plan={interestPairModal.plan}
					onProceed={interestPairModal.onProceed ?? closeInterestPairModal}
					onClose={closeInterestPairModal}
					onDeletePair={onDeletePair}
				/>
			) : null}
		</Drawer>
	);
};
